import type { ReportCache } from './reportCache.js';
import type { ApiClient } from './runtime.js';
import type { ReportDom, ReportType, RowWithClient, SortDir } from './types.js';
import { clientSummaryHtml, esc, fmtDate, padFn, writeClipboard } from './utils.js';

export type ReportRunDeps = {
  setLoading: (on: boolean) => void;
  onComplete: () => void;
};

export type SortAccessor<TRow> = (key: string) => (r: TRow) => unknown;

export interface RunnableReport {
  readonly reportType: ReportType;
  exportCsv(): void;
  copyClientSummary(): void;
  showClientSummary(): void;
}

export abstract class BaseReport<TRow extends RowWithClient> implements RunnableReport {
  protected lastData: TRow[] | null = null;
  protected lastRanAt: Date | null = null;
  protected lastCachedAt: Date | null = null;
  protected sortKey: string | null = null;
  protected sortDir: SortDir = 'asc';
  protected dataArea: HTMLElement | null = null;
  private summaryModalCleanup: (() => void) | null = null;

  constructor(
    public readonly reportType: ReportType,
    protected readonly cache: ReportCache,
    protected readonly apiClient: ApiClient,
    protected readonly dom: ReportDom,
    protected readonly deps: ReportRunDeps,
  ) {}

  abstract get reportLabel(): string;
  protected abstract buildCacheKey(slugs: string[]): string;
  protected abstract fetchData(slugs: string[]): Promise<TRow[]>;
  protected abstract resetFilterState(): void;
  protected abstract peopleAccessor(): (r: TRow) => number;
  protected abstract renderToolbar(toolbar: HTMLElement): void;
  protected abstract renderData(area: HTMLElement, rows: TRow[], filtered: TRow[]): void;
  protected abstract renderTitle(rows: TRow[], filtered: TRow[]): void;
  abstract exportCsv(): void;

  hasData(): boolean {
    return !!this.lastData && this.lastData.length > 0;
  }

  /**
   * Full rebuild — toolbar + data area. Called when fresh data arrives.
   */
  render(): void {
    if (!this.lastData) return;
    this.dom.panel.classList.remove('hidden');
    this.dom.copyBtn.classList.remove('hidden');
    this.dom.showBtn.classList.remove('hidden');
    this.dom.exportBtn.classList.remove('hidden');

    this.dom.container.innerHTML = '';

    const toolbar = document.createElement('div');
    toolbar.className = 'results-toolbar';
    this.dom.container.appendChild(toolbar);
    this.renderToolbar(toolbar);

    const dataArea = document.createElement('div');
    this.dom.container.appendChild(dataArea);
    this.dataArea = dataArea;

    this.refreshView();
  }

  /**
   * Re-render only the title + data area. Called on filter/sort/toggle
   * changes so the toolbar (and its focused inputs) stay intact.
   */
  protected refreshView(): void {
    if (!this.lastData) return;
    const filtered = this.filterRows(this.lastData);
    this.renderTitle(this.lastData, filtered);
    if (this.dataArea) {
      this.dataArea.innerHTML = '';
      this.renderData(this.dataArea, this.lastData, filtered);
    }
  }

  async run(slugs: string[]): Promise<void> {
    if (slugs.length === 0) {
      this.showError('Select at least one client.');
      return;
    }
    const cacheKey = this.buildCacheKey(slugs);
    const cached = this.cache.get<TRow[]>(cacheKey);
    if (cached) {
      console.debug(`[report-cache] HIT ${this.reportType}`);
      this.applyData(cached.data, new Date(), cached.fetchedAt);
      this.render();
      this.deps.onComplete();
      return;
    }

    this.deps.setLoading(true);
    this.dom.panel.classList.add('hidden');

    try {
      const data = await this.fetchData(slugs);
      const now = new Date();
      this.cache.set<TRow[]>(cacheKey, { data, fetchedAt: now });
      console.debug(`[report-cache] STORE ${this.reportType}`);
      this.applyData(data, now, now);
      this.render();
      this.deps.onComplete();
    } catch (err) {
      this.showError(err instanceof Error ? err.message : String(err));
    } finally {
      this.deps.setLoading(false);
    }
  }

  clearState(): void {
    this.lastData = null;
    this.lastRanAt = null;
    this.lastCachedAt = null;
    this.sortKey = null;
    this.sortDir = 'asc';
    this.dataArea = null;
  }

  copyClientSummary(): void {
    const text = this.buildClientSummaryText();
    if (!text) return;
    this.copyTextWithFlash(this.dom.copyBtn, text);
  }

  showClientSummary(): void {
    const text = this.buildClientSummaryText();
    if (!text) return;
    this.openSummaryModal(text);
  }

  private buildClientSummaryText(): string | null {
    if (!this.lastData || this.lastData.length === 0) return null;
    const filtered = this.filterRows(this.lastData);
    const peopleAccessor = this.peopleAccessor();
    const channelAccessor = this.channelAccessor();
    return channelAccessor
      ? this.buildChannelSplitSummary(filtered, peopleAccessor, channelAccessor)
      : this.buildFlatSummary(filtered, peopleAccessor);
  }

  private copyTextWithFlash(btn: HTMLElement, text: string): void {
    const orig = btn.innerHTML;
    const flash = (label: string): void => {
      btn.textContent = label;
      setTimeout(() => {
        btn.innerHTML = orig;
      }, 1500);
    };
    void writeClipboard(text)
      .then(() => flash('Copied!'))
      .catch(() => flash('Copy failed'));
  }

  private openSummaryModal(text: string): void {
    const modalRoot = this.dom.panel.getRootNode();
    const trigger = this.getActiveElement(modalRoot);
    this.summaryModalCleanup?.();
    this.queryModalRoot(modalRoot)?.querySelector('.summary-modal-overlay')?.remove();

    const overlay = document.createElement('div');
    overlay.className = 'summary-modal-overlay';

    const modal = document.createElement('div');
    modal.className = 'summary-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Client Summary');

    const header = document.createElement('div');
    header.className = 'summary-modal-header';

    const title = document.createElement('h3');
    title.textContent = `${this.reportLabel} Report — Client Summary`;
    header.appendChild(title);

    const headerActions = document.createElement('div');
    headerActions.className = 'summary-modal-actions';

    const copyBtn = document.createElement('button');
    copyBtn.type = 'button';
    copyBtn.className = 'btn btn-ghost';
    copyBtn.innerHTML = `
      <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
        <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/>
        <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/>
      </svg>
      Copy
    `;
    copyBtn.addEventListener('click', () => this.copyTextWithFlash(copyBtn, text));

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'summary-modal-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';

    headerActions.appendChild(copyBtn);
    headerActions.appendChild(closeBtn);
    header.appendChild(headerActions);

    const body = document.createElement('div');
    body.className = 'summary-modal-body';
    const pre = document.createElement('pre');
    pre.className = 'summary-modal-code';
    const code = document.createElement('code');
    code.textContent = text;
    pre.appendChild(code);
    body.appendChild(pre);

    modal.appendChild(header);
    modal.appendChild(body);
    overlay.appendChild(modal);

    const close = (): void => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
      this.summaryModalCleanup = null;
      trigger?.focus();
    };
    const FOCUSABLE_SELECTOR =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) { e.preventDefault(); return; }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) { e.preventDefault(); return; }
        const activeElement = this.getActiveElement(modalRoot);
        if (e.shiftKey) {
          if (activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
    };
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    document.addEventListener('keydown', onKey);
    this.summaryModalCleanup = close;

    this.modalMount(modalRoot).appendChild(overlay);
    closeBtn.focus();
  }

  private queryModalRoot(root: Node): Document | ShadowRoot | null {
    return root instanceof Document || root instanceof ShadowRoot ? root : null;
  }

  private modalMount(root: Node): HTMLElement | ShadowRoot {
    return root instanceof ShadowRoot ? root : document.body;
  }

  private getActiveElement(root: Node): HTMLElement | null {
    const activeElement =
      root instanceof ShadowRoot ? root.activeElement : document.activeElement;
    return activeElement instanceof HTMLElement ? activeElement : null;
  }

  protected channelAccessor(): ((r: TRow) => string) | null {
    return null;
  }

  private buildFlatSummary(filtered: TRow[], peopleAccessor: (r: TRow) => number): string {
    const counts = new Map<string, number>();
    const peopleCounts = new Map<string, number>();
    for (const r of filtered) {
      const slug = r.client_slug;
      counts.set(slug, (counts.get(slug) ?? 0) + 1);
      peopleCounts.set(slug, (peopleCounts.get(slug) ?? 0) + (peopleAccessor(r) || 0));
    }

    const entries = [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    const totalItems = entries.reduce((s, [, n]) => s + n, 0);
    const totalPeople = entries.reduce((s, [slug]) => s + (peopleCounts.get(slug) ?? 0), 0);

    const ranAt = this.lastRanAt ?? new Date();
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const dateStr = `${ranAt.getFullYear()}-${pad2(ranAt.getMonth() + 1)}-${pad2(ranAt.getDate())} ${pad2(ranAt.getHours())}:${pad2(ranAt.getMinutes())}`;
    const titleLine = `${this.reportLabel.toUpperCase()} REPORT CLIENT SUMMARY`;

    const col1 = 'client';
    const col2 = this.summaryItemLabel();
    const col3 = 'num_people';
    const dataRows = entries.map(([slug, n]) => [
      slug,
      n.toLocaleString(),
      (peopleCounts.get(slug) ?? 0).toLocaleString(),
    ] as const);
    const totalRow = ['total', totalItems.toLocaleString(), totalPeople.toLocaleString()] as const;

    let w1 = Math.max(col1.length, totalRow[0].length, ...dataRows.map((r) => r[0].length));
    const w2 = Math.max(col2.length, totalRow[1].length, ...dataRows.map((r) => r[1].length));
    const w3 = Math.max(col3.length, totalRow[2].length, ...dataRows.map((r) => r[2].length));

    const longestHdr = Math.max(titleLine.length, dateStr.length);
    const currentInner = w1 + w2 + w3 + 8; // 8 = " | " * 2 + "| " + " |"
    if (currentInner < longestHdr + 2) w1 += longestHdr + 2 - currentInner;

    const sep = `+-${'-'.repeat(w1)}-+-${'-'.repeat(w2)}-+-${'-'.repeat(w3)}-+`;
    const row = (a: string, b: string, c: string) =>
      `| ${padFn(a, w1, false)} | ${padFn(b, w2, true)} | ${padFn(c, w3, true)} |`;

    const tableInner = sep.length - 2;
    const centerPad = (s: string) => {
      const left = Math.floor((tableInner - s.length) / 2);
      return '|' + ' '.repeat(left) + s + ' '.repeat(tableInner - left - s.length) + '|';
    };
    const lines = [sep, centerPad(titleLine), centerPad(dateStr), sep, row(col1, col2, col3), sep];
    for (const [slug, n, p] of dataRows) lines.push(row(slug, n, p));
    lines.push(sep);
    lines.push(row(totalRow[0], totalRow[1], totalRow[2]));
    lines.push(sep);

    return lines.join('\n');
  }

  private buildChannelSplitSummary(
    filtered: TRow[],
    peopleAccessor: (r: TRow) => number,
    channelAccessor: (r: TRow) => string,
  ): string {
    const CHANNELS = ['EML', 'SMS'] as const;
    type Cell = { items: number; people: number };
    const zero = (): Cell => ({ items: 0, people: 0 });

    const perClient = new Map<string, Map<string, Cell>>();
    const channelTotals = new Map<string, Cell>();
    for (const ch of CHANNELS) channelTotals.set(ch, zero());
    const grandTotal = zero();

    for (const r of filtered) {
      const slug = r.client_slug;
      const channel = channelAccessor(r);
      if (!(CHANNELS as readonly string[]).includes(channel)) continue;

      let cm = perClient.get(slug);
      if (!cm) {
        cm = new Map();
        for (const c of CHANNELS) cm.set(c, zero());
        perClient.set(slug, cm);
      }
      const cell = cm.get(channel)!;
      const ppl = peopleAccessor(r) || 0;
      cell.items += 1;
      cell.people += ppl;

      const chTot = channelTotals.get(channel)!;
      chTot.items += 1;
      chTot.people += ppl;

      grandTotal.items += 1;
      grandTotal.people += ppl;
    }

    const fmt = (n: number) => n.toLocaleString();
    const slugs = [...perClient.keys()].sort((a, b) => a.localeCompare(b));

    type RowCells = [string, string][];
    const dataRows: { client: string; cells: RowCells }[] = slugs.map((slug) => {
      const cm = perClient.get(slug)!;
      const cells: RowCells = [];
      let ti = 0;
      let tp = 0;
      for (const ch of CHANNELS) {
        const cell = cm.get(ch)!;
        cells.push([fmt(cell.items), fmt(cell.people)]);
        ti += cell.items;
        tp += cell.people;
      }
      cells.push([fmt(ti), fmt(tp)]);
      return { client: slug, cells };
    });

    const totalCells: RowCells = [
      ...CHANNELS.map((ch) => {
        const c = channelTotals.get(ch)!;
        return [fmt(c.items), fmt(c.people)] as [string, string];
      }),
      [fmt(grandTotal.items), fmt(grandTotal.people)],
    ];

    const GROUPS = ['EML', 'SMS', 'total'] as const;
    const SUB_CAMPS = '#cams';
    const SUB_PEOPLE = '#ppl';

    let wClient = Math.max('client'.length, 'total'.length, ...slugs.map((s) => s.length));

    const groupWidths: [number, number][] = GROUPS.map((_, gi) => {
      const allCamps = [
        SUB_CAMPS.length,
        ...dataRows.map((r) => r.cells[gi]![0].length),
        totalCells[gi]![0].length,
      ];
      const allPpl = [
        SUB_PEOPLE.length,
        ...dataRows.map((r) => r.cells[gi]![1].length),
        totalCells[gi]![1].length,
      ];
      return [Math.max(...allCamps), Math.max(...allPpl)];
    });

    // Widen sub-cols if the group label is longer than they fit
    for (let gi = 0; gi < GROUPS.length; gi++) {
      const [wc, wp] = groupWidths[gi]!;
      const inner = wc + wp + 3; // 3 = " | " between sub-cols
      const label = GROUPS[gi]!;
      if (label.length > inner) {
        const extra = label.length - inner;
        const addCamp = Math.ceil(extra / 2);
        groupWidths[gi] = [wc + addCamp, wp + (extra - addCamp)];
      }
    }

    const ranAt = this.lastRanAt ?? new Date();
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const dateStr = `${ranAt.getFullYear()}-${pad2(ranAt.getMonth() + 1)}-${pad2(ranAt.getDate())} ${pad2(ranAt.getHours())}:${pad2(ranAt.getMinutes())}`;
    const titleLine = `${this.reportLabel.toUpperCase()} REPORT CLIENT SUMMARY`;

    // Widen client col if needed so the title/date row fits
    const groupsContribution = groupWidths.reduce((a, [wc, wp]) => a + wc + wp + 6, 0);
    let innerWidth = 2 + wClient + groupsContribution;
    const longestHdr = Math.max(titleLine.length, dateStr.length);
    if (innerWidth < longestHdr + 2) {
      wClient += longestHdr + 2 - innerWidth;
      innerWidth = longestHdr + 2;
    }

    const sep =
      '+' + '-'.repeat(wClient + 2) +
      groupWidths.map(([wc, wp]) => '+' + '-'.repeat(wc + 2) + '+' + '-'.repeat(wp + 2)).join('') +
      '+';

    const groupHeader =
      '| ' + padFn('', wClient, false) + ' ' +
      groupWidths
        .map(([wc, wp], gi) => {
          const inner = wc + wp + 3;
          const label = GROUPS[gi]!;
          const left = Math.floor((inner - label.length) / 2);
          const right = inner - left - label.length;
          return '| ' + ' '.repeat(left) + label + ' '.repeat(right) + ' ';
        })
        .join('') +
      '|';

    const subHeader =
      '| ' + padFn('client', wClient, false) + ' ' +
      groupWidths
        .map(
          ([wc, wp]) =>
            '| ' + padFn(SUB_CAMPS, wc, true) + ' | ' + padFn(SUB_PEOPLE, wp, true) + ' ',
        )
        .join('') +
      '|';

    const buildDataLine = (client: string, cells: RowCells): string =>
      '| ' + padFn(client, wClient, false) + ' ' +
      cells
        .map(([c, p], gi) => {
          const [wc, wp] = groupWidths[gi]!;
          return '| ' + padFn(c, wc, true) + ' | ' + padFn(p, wp, true) + ' ';
        })
        .join('') +
      '|';

    const tableInner = sep.length - 2;
    const centerPad = (s: string) => {
      const left = Math.floor((tableInner - s.length) / 2);
      return '|' + ' '.repeat(left) + s + ' '.repeat(tableInner - left - s.length) + '|';
    };

    const lines = [
      sep,
      centerPad(titleLine),
      centerPad(dateStr),
      sep,
      groupHeader,
      subHeader,
      sep,
      ...dataRows.map((r) => buildDataLine(r.client, r.cells)),
      sep,
      buildDataLine('total', totalCells),
      sep,
    ];

    return lines.join('\n');
  }

  protected abstract filterRows(rows: TRow[]): TRow[];
  protected abstract summaryItemLabel(): string;

  protected applyData(data: TRow[], ranAt: Date, cachedAt: Date): void {
    this.lastData = data;
    this.lastRanAt = ranAt;
    this.lastCachedAt = cachedAt;
    this.resetFilterState();
    this.sortKey = null;
    this.sortDir = 'asc';
  }

  protected sortRows(rows: TRow[], accessor: SortAccessor<TRow>): TRow[] {
    if (!this.sortKey) return rows;
    const get = accessor(this.sortKey);
    const sorted = [...rows].sort((a, b) => {
      let va = get(a);
      let vb = get(b);
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (typeof va === 'number' && typeof vb === 'number') return va - vb;
      return String(va).localeCompare(String(vb), undefined, { numeric: true });
    });
    return this.sortDir === 'desc' ? sorted.reverse() : sorted;
  }

  protected toggleSort(key: string): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.refreshView();
  }

  protected sortIndicator(key: string): string {
    if (this.sortKey !== key) return ' ↕';
    return this.sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  protected thSortable(key: string, label: string, align?: 'right'): string {
    const style = align === 'right' ? ' style="text-align:right"' : '';
    return `<th${style} class="sortable" data-sort-key="${esc(key)}">${esc(label)}${this.sortIndicator(key)}</th>`;
  }

  protected bindSortHeaders(table: HTMLElement): void {
    table.querySelectorAll<HTMLElement>('th.sortable').forEach((th) => {
      th.addEventListener('click', () => {
        const key = th.dataset.sortKey;
        if (key) this.toggleSort(key);
      });
    });
  }

  protected appendClientSummary(parent: HTMLElement, filtered: TRow[]): void {
    const html = clientSummaryHtml(filtered, { peopleAccessor: this.peopleAccessor() });
    if (!html) return;
    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    const child = wrap.firstElementChild;
    if (child) parent.appendChild(child);
  }

  protected reportTimestampHtml(): string {
    if (!this.lastRanAt) return '';
    const ranStr = fmtDate(this.lastRanAt);
    const cachedStr = fmtDate(this.lastCachedAt);
    const isCached =
      !!this.lastCachedAt && this.lastRanAt.getTime() !== this.lastCachedAt.getTime();
    return `<span class="report-ts">Ran: ${esc(ranStr)}${isCached ? `  —  Cached: ${esc(cachedStr)}` : ''}</span>`;
  }

  protected showError(msg: string): void {
    this.lastData = null;
    this.dom.copyBtn.classList.add('hidden');
    this.dom.showBtn.classList.add('hidden');
    this.dom.exportBtn.classList.add('hidden');
    this.dom.title.textContent = 'Error';
    this.dom.panel.classList.remove('hidden');
    this.dom.container.innerHTML = `<div class="error-msg">${esc(msg)}</div>`;
  }
}
